import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AlertController, IonicModule } from '@ionic/angular';
import { OAuthService } from 'angular-oauth2-oidc';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { ENGEDETT_FAJLOK, idoKiir, meretKiir } from './beadando-kozos';

interface Gyerek { oktazon: string; nev: string; osztaly: string; }

/**
 *  FELADATAIM – a tanuló és a gondviselő képernyője (beadandó feladatok).
 *
 *  A tanuló a saját feladatait látja, és BEADJA őket (szöveg + fájlok); a beadás a határidőig
 *  módosítható, utána csak akkor, ha a tanár engedi a késői beadást – ilyenkor „késve”
 *  jelölést kap. A gondviselő a gyermeke feladatait, beadásait és a tanár visszajelzését
 *  látja, de beadni nem tud (a kiszolgáló is csak a tanuló saját belépésével fogadja).
 */
@Component({
  selector: 'app-feladataim',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './feladataim.component.html',
  styleUrls: ['./beadandok.component.scss'],
})
export class FeladataimComponent implements OnInit {
  intezmeny = '';
  toltodik = true;
  hiba = '';
  tanuloNezet = false;
  gyerekek: Gyerek[] = [];
  kivalasztott = '';
  feladatok: any[] = [];
  sajat = false;
  listaToltodik = false;
  szuro: 'nyitott' | 'mind' = 'nyitott';
  kuld: Record<string, boolean> = {};
  readonly fajlTipusok = ENGEDETT_FAJLOK;
  readonly idoKiir = idoKiir;
  readonly meretKiir = meretKiir;

  constructor(private http: HttpClient, private oauth: OAuthService, private data: DataService, private alert: AlertController) {}

  async ngOnInit() {
    try { await this.oauth.loadUserProfile(); } catch { /* a kilét a tárolt adatból is feloldható */ }
    const user = this.data.belepoKilet(this.oauth.getIdentityClaims());
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(sajat?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    if (!user?.username || !this.intezmeny) {
      this.hiba = 'Nem azonosítható a belépés vagy az intézmény.';
      this.toltodik = false;
      return;
    }
    try {
      const lista: any[] = await firstValueFrom(this.http.get<any[]>(
        `/api/nyilvantartas/szulogyereke/${user.username}/${this.intezmeny}`)) || [];
      const sajatSor = this.data.sajatTanulo(lista);
      this.tanuloNezet = !!sajatSor;
      //  A felügyeleti jogot nem gyakorló szülő (felugyeleti_jog jelölés) a beadandókat nem látja.
      this.gyerekek = (sajatSor ? [sajatSor] : lista.filter((g) => g?.oktazon && !g?.felugyeleti_jog)).map((g) => ({
        oktazon: String(g.oktazon), nev: g.full_name || g.name || String(g.oktazon), osztaly: g.aktualis_osztaly || g.osztaly || '',
      }));
      this.kivalasztott = this.gyerekek[0]?.oktazon || '';
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni a tanulók listáját.';
    } finally {
      this.toltodik = false;
    }
    if (this.kivalasztott) await this.betolt();
  }

  async betolt() {
    if (!this.kivalasztott) return;
    const kert = this.kivalasztott;
    this.listaToltodik = true;
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/beadando/tanulo/${encodeURIComponent(kert)}/feladatok/${this.intezmeny}`));
      if (kert !== this.kivalasztott) return;      // közben másik gyermeket választottak
      this.sajat = !!r?.sajat;
      this.feladatok = (r?.feladatok || []).map((f: any) => ({ ...f, ujSzoveg: f.beadas?.szoveg || '', ujFajlok: [] as File[] }));
      this.hiba = '';
    } catch (e: any) {
      this.feladatok = [];
      this.hiba = e?.error?.error || 'A feladatok nem tölthetők be.';
    } finally {
      this.listaToltodik = false;
    }
  }

  gyerekValtas(okt: string) { this.kivalasztott = okt; this.feladatok = []; this.betolt(); }

  /** Aktuális: még nincs visszajelzés, és még beadható (vagy már be van adva). */
  get lathato(): any[] {
    if (this.szuro === 'mind') return this.feladatok;
    return this.feladatok.filter((f) => !f.beadas?.visszajelzes && !f.beadas?.ertekeles && (!f.lejart || f.keso_beadas || !!f.beadas));
  }

  allapot(f: any): { szoveg: string; szin: string } {
    if (f.beadas?.visszajelzes || f.beadas?.ertekeles) return { szoveg: 'Visszajelzés érkezett', szin: 'success' };
    if (f.beadas?.beadva) return f.beadas.keso ? { szoveg: 'Késve beadva', szin: 'warning' } : { szoveg: 'Beadva', szin: 'primary' };
    if (f.lejart) return { szoveg: f.keso_beadas ? 'Lejárt – késve még beadható' : 'Lejárt, nincs beadva', szin: 'danger' };
    return { szoveg: 'Beadandó', szin: 'medium' };
  }

  fajlValasztas(f: any, ev: any) {
    const lista: File[] = Array.from(ev?.target?.files || []);
    const nagy = lista.find((x) => x.size > 20 * 1024 * 1024);
    if (nagy) { this.data.sendToast(`A(z) „${nagy.name}” nagyobb 20 MB-nál.`, 'warning'); ev.target.value = ''; f.ujFajlok = []; return; }
    if (lista.length > 5) { this.data.sendToast('Egyszerre legfeljebb 5 fájl tölthető fel.', 'warning'); ev.target.value = ''; f.ujFajlok = []; return; }
    f.ujFajlok = lista;
  }

  async beadas(f: any, input?: HTMLInputElement) {
    if (!this.sajat || !f.beadhato || this.kuld[f.id]) return;
    if (!String(f.ujSzoveg || '').trim() && !f.ujFajlok.length && !f.beadas?.fajlok?.length) {
      this.data.sendToast('Írj választ, vagy csatolj legalább egy fájlt.', 'warning');
      return;
    }
    if (f.lejart) {
      const a = await this.alert.create({
        header: 'Késői beadás',
        message: 'A határidő lejárt. A beadás „késve” jelölést kap. Beadod?',
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Beadom', role: 'confirm' }],
      });
      await a.present();
      if ((await a.onDidDismiss()).role !== 'confirm') return;
    }
    const fd = new FormData();
    fd.append('szoveg', f.ujSzoveg || '');
    for (const x of f.ujFajlok) fd.append('fajlok', x, x.name);
    this.kuld[f.id] = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/beadando/tanulo/${encodeURIComponent(this.kivalasztott)}/beadas/${f.id}`, fd));
      this.data.sendToast(r?.keso ? 'Beadva (késve).' : 'Beadva.');
      if (input) input.value = '';
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A beadás nem sikerült.', 'error');
    } finally {
      this.kuld[f.id] = false;
    }
  }

  async fajlTorles(f: any, fajl: any) {
    const a = await this.alert.create({
      header: 'Fájl törlése',
      message: `Törlöd a(z) „${fajl.fajlnev}” fájlt a beadásból?`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'destructive') return;
    try {
      const r: any = await firstValueFrom(this.http.delete(
        `/api/beadando/tanulo/${encodeURIComponent(this.kivalasztott)}/fajl/${fajl.id}`));
      this.data.sendToast(r?.visszavonva ? 'A fájl törölve – a beadás visszavonva.' : 'A fájl törölve.');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A fájl nem törölhető.', 'error');
    }
  }

  fajlMegnyit(fajl: any) {
    this.data.megnyitFajl(`/api/beadando/tanulo/${encodeURIComponent(this.kivalasztott)}/fajl/${fajl.id}`, fajl.fajlnev);
  }

  csatolmanyMegnyit(f: any) {
    if (f.csatolmany) this.data.megnyitFajl(`/api/beadando/tanulo/${encodeURIComponent(this.kivalasztott)}/csatolmany/${f.id}`, f.csatolmany.nev);
  }
}
